import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import GenericModal from '../../components/agv-generic-modal';
import InputCombobox from '../../components/agv-forms-combobox/input-combobox';
import InputRadio from '../../components/agv-forms-radio';
import InputText from '../../components/agv-forms-input';
import FieldTitle from '../../components/agv-forms-title';
import { ROLE_ENUM, ROLE_ENUM_DISPLAY } from '../../enums/role-enum.enum';
import isEmailValid from '../../utils/is-email-valid.util';
import isPhoneNumberValid from '../../utils/is-phone-number-valid.util';

export default function EditUserModal({
  isOpen,
  setIsOpen,
  user,
  editUser,
  deleteUser,
  mainActionIsLoading,
  secondaryActionIsLoading,
  clientsData,
  ownClientFactories,
  features,
}) {
  const config = useSelector((state) => state.config);

  const needToAssociateFactory =
    features?.OTHERS?.BACKOFFICE?.needToAssociateFactory ?? false;

  const [clientId, setClientId] = useState(
    config?.is_super_admin ? config?.client_id?.toString() : null
  );
  const [factory, setFactory] = useState(user?.factory_id);
  const [accessibleFactories, setAccessibleFactories] = useState(
    user?.config?.accessible_factory_ids ??
      (user?.factory_id ? [user.factory_id] : [])
  );
  const [lastname, setLastname] = useState(user?.last_name);
  const [firstname, setFirstname] = useState(user?.first_name);
  const [email, setEmail] = useState(user?.email);
  const [phone, setPhone] = useState(user?.phone_number?.replace('+33', '0'));
  const [role, setRole] = useState(user?.roles[0]);
  const [clientFactories, setClientFactories] = useState([]);

  const isEmpty =
    !isEmailValid(email) ||
    !isPhoneNumberValid(phone) ||
    (needToAssociateFactory && clientFactories?.length > 0 && !factory) ||
    !lastname?.length ||
    !firstname?.length ||
    (config?.is_super_admin ? !clientId : false);

  const initialAccessibleFactories =
    user?.config?.accessible_factory_ids ??
    (user?.factory_id ? [user.factory_id] : []);

  const isSameValues =
    (config?.is_super_admin
      ? clientId === config?.client_id?.toString()
      : true) &&
    email === user?.email &&
    factory === user?.factory_id &&
    lastname === user?.last_name &&
    firstname === user?.first_name &&
    role === user?.roles[0] &&
    phone === user?.phone_number?.replace('+33', '0') &&
    JSON.stringify([...accessibleFactories].sort()) ===
      JSON.stringify([...initialAccessibleFactories].sort());

  const DISABLED = isEmpty || isSameValues;

  useEffect(() => {
    if (config?.is_super_admin) {
      const factories = clientsData?.filter(
        (clientData) => clientData?.client_id === clientId
      )?.[0]?.factories;
      setClientFactories(factories);
    } else {
      setClientFactories(ownClientFactories ?? []);
    }
  }, [clientsData, clientId, ownClientFactories, config?.is_super_admin]);

  useEffect(() => {
    if (factory != null && !accessibleFactories.includes(factory)) {
      setAccessibleFactories((prev) => [...prev, factory]);
    }
  }, [factory]);

  useEffect(() => {
    if (isOpen) {
      setClientId(config?.is_super_admin ? user?.client_id : null);
      setFactory(user?.factory_id);
      setAccessibleFactories(
        user?.config?.accessible_factory_ids ??
          (user?.factory_id ? [user.factory_id] : [])
      );
      setLastname(user?.last_name);
      setFirstname(user?.first_name);
      setEmail(user?.email);
      setPhone(user?.phone_number?.replace('+33', '0'));
      setRole(user?.roles[0]);
    }
  }, [isOpen]);

  const handleDeleteUser = () => {
    if (
      window.confirm(
        "Êtes-vous sûr de vouloir procéder à la suppression de l'utilisateur ? Cette action est irréversible."
      )
    ) {
      deleteUser(user?.id, clientId);
    }
  };

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={'Modifier un utilisateur'}
      mainAction={() =>
        editUser(
          user?.id,
          config?.is_super_admin ? clientId : null,
          firstname,
          lastname,
          email,
          phone,
          factory,
          accessibleFactories,
          role
        )
      }
      mainActionText={'Modifier'}
      mainActionIsLoading={mainActionIsLoading}
      mainActionDisabled={DISABLED}
      secondaryAction={handleDeleteUser}
      secondaryActionIsLoading={secondaryActionIsLoading}
      secondaryActionText={"Supprimer l'utilisateur"}
    >
      <div className="tw-flex tw-flex-col tw-my-4">
        {config?.is_super_admin && (
          <InputCombobox
            value={clientId}
            setValue={setClientId}
            fieldTitle={'ID Client'}
            items={clientsData?.map((client) => ({
              name: `${client.client_id} - ${client.name}`,
              value: client.client_id,
            }))}
            isRequired
            emptyError={`Le champ "Client ID" ne peut être vide.`}
            style={''}
          />
        )}
        <InputText
          value={lastname}
          setValue={setLastname}
          fieldTitle={'Nom'}
          isRequired
          emptyError={`Le champ "Nom" ne peut être vide.`}
          style={'tw-mt-6'}
          placeholder={'Leblanc'}
        />
        <InputText
          value={firstname}
          setValue={setFirstname}
          fieldTitle={'Prénom'}
          isRequired
          emptyError={`Le champ "Prénom" ne peut être vide.`}
          style={'tw-mt-6'}
          placeholder={'Juste'}
        />
        <InputText
          value={email}
          setValue={setEmail}
          fieldTitle={'E-mail'}
          isRequired
          emptyError={`Le champ "E-mail" ne peut être vide.`}
          style={'tw-mt-6'}
          placeholder={'exemple@exemple.com'}
          isBadFormat={!isEmailValid(email)}
          type="email"
        />
        <InputText
          value={phone}
          setValue={setPhone}
          fieldTitle={'Téléphone'}
          isRequired
          emptyError={`Le champ "Téléphone" ne peut être vide.`}
          style={'tw-mt-6'}
          placeholder={'0612345678'}
          isBadFormat={!isPhoneNumberValid(phone)}
          type="tel"
          maxLength={10}
        />
        {needToAssociateFactory &&
          clientFactories &&
          clientFactories.length > 0 && (
            <>
              <InputCombobox
                value={factory}
                setValue={setFactory}
                fieldTitle={'Usine de rattachement'}
                items={clientFactories?.map((element) => ({
                  name: element.name,
                  value: element.id,
                }))}
                isRequired
                emptyError={`Le champ "Usine de rattachement" ne peut être vide.`}
                style={'tw-mt-6'}
                isDisabled={
                  config?.is_super_admin
                    ? !clientId || !clientFactories || !clientFactories?.length
                    : false
                }
              />
              <div className="tw-mt-6">
                <FieldTitle title="Usines accessibles" />
                {clientFactories.map((f) => (
                  <label
                    key={f.id}
                    className="tw-flex tw-items-center tw-gap-2 tw-mb-1.5 tw-cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={accessibleFactories.includes(f.id)}
                      disabled={f.id === factory}
                      onChange={() =>
                        setAccessibleFactories((prev) =>
                          prev.includes(f.id)
                            ? prev.filter((id) => id !== f.id)
                            : [...prev, f.id]
                        )
                      }
                      className="tw-cursor-pointer"
                    />
                    <span className="tw-text-sm tw-text-text-dark-medium-importance">
                      {f.name}
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        <InputRadio
          fieldTitle={'Rôle'}
          value={role}
          setValue={setRole}
          style={'tw-mt-6'}
          items={[
            {
              value: ROLE_ENUM.ADMIN,
              name: ROLE_ENUM_DISPLAY[ROLE_ENUM.ADMIN],
            },
            {
              value: ROLE_ENUM.OPERATOR,
              name: ROLE_ENUM_DISPLAY[ROLE_ENUM.OPERATOR],
            },
          ]}
        />
      </div>
    </GenericModal>
  );
}
